//frontend/src/components/MesDossiers.jsx

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Button, Form, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaPlus, FaEye, FaFileAlt, FaClock } from 'react-icons/fa';
import api from '../services/api';
import { toast } from 'react-toastify';

const MesDossiers = () => {
    const [dossiers, setDossiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const navigate = useNavigate();

    useEffect(() => {
        loadDossiers();
    }, []);

    const loadDossiers = async () => {
        setLoading(true);
        try {
            const response = await api.get('/dossiers/');
            console.log('Dossiers loaded:', response.data);
            
            // L'API renvoie un objet paginé avec les résultats dans 'results'
            let dossiersData = [];
            
            if (response.data.results) {
                // Si c'est un objet paginé
                dossiersData = Array.isArray(response.data.results) ? response.data.results : [];
            } else if (Array.isArray(response.data)) {
                // Si c'est directement un tableau
                dossiersData = response.data;
            }
            
            console.log('Dossiers processed:', dossiersData);
            setDossiers(dossiersData);
        } catch (error) {
            console.error('Error loading dossiers:', error);
            toast.error('Erreur lors du chargement des dossiers');
            setDossiers([]);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const variants = {
            draft: 'secondary',
            submitted: 'info',
            in_review: 'warning',
            approved: 'success',
            rejected: 'danger',
        };
        
        const labels = {
            draft: 'Brouillon',
            submitted: 'Soumis',
            in_review: 'En révision',
            approved: 'Approuvé',
            rejected: 'Rejeté',
        };

        return <Badge bg={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
    };

    const getVisaTypeLabel = (type) => {
        const types = {
            work: 'Visa de Travail',
            study: 'Visa d\'Études',
            tourist: 'Visa Touristique',
            family: 'Regroupement Familial',
            business: 'Visa d\'Affaires',
        };
        return types[type] || type;
    };

    const getVisaTypeIcon = (type) => {
        const icons = {
            work: '💼',
            study: '🎓',
            tourist: '✈️',
            family: '👨‍👩‍👧‍👦',
            business: '🏢',
        };
        return icons[type] || '📄';
    };

    const filteredDossiers = dossiers.filter(dossier => {
        const matchesSearch = dossier.id.toString().includes(searchTerm) ||
                            getVisaTypeLabel(dossier.visa_type).toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterStatus === 'all' || dossier.status === filterStatus;
        return matchesSearch && matchesFilter;
    });

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Chargement de vos dossiers...</p>
            </Container>
        );
    }

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h2>
                        <FaFileAlt className="me-3" />
                        Mes Dossiers
                    </h2>
                    <p className="text-muted">Gérez tous vos dossiers de demande de visa</p>
                </Col>
                <Col className="text-end">
                    <Button variant="primary" onClick={() => navigate('/dossier/create')}>
                        <FaPlus className="me-2" />
                        Nouveau dossier
                    </Button>
                </Col>
            </Row>

            {/* Statistiques rapides */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <h3 className="text-primary">{dossiers.length}</h3>
                            <small className="text-muted">Total</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <h3 className="text-secondary">{dossiers.filter(d => d.status === 'draft').length}</h3>
                            <small className="text-muted">Brouillons</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <h3 className="text-warning">{dossiers.filter(d => d.status === 'submitted' || d.status === 'in_review').length}</h3>
                            <small className="text-muted">En cours</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <h3 className="text-success">{dossiers.filter(d => d.status === 'approved').length}</h3>
                            <small className="text-muted">Approuvés</small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Filtres */}
            <Row className="mb-4">
                <Col md={6}>
                    <InputGroup>
                        <InputGroup.Text>
                            <FaSearch />
                        </InputGroup.Text>
                        <Form.Control
                            placeholder="Rechercher par numéro ou type..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </InputGroup>
                </Col>
                <Col md={6}>
                    <Form.Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                        <option value="all">Tous les statuts</option>
                        <option value="draft">Brouillon</option>
                        <option value="submitted">Soumis</option>
                        <option value="in_review">En révision</option>
                        <option value="approved">Approuvé</option>
                        <option value="rejected">Rejeté</option>
                    </Form.Select>
                </Col>
            </Row>

            {/* Liste des dossiers */}
            {filteredDossiers.length === 0 ? (
                <Card className="text-center py-5">
                    <Card.Body>
                        {dossiers.length === 0 ? (
                            <>
                                <FaFileAlt size={60} className="text-muted mb-3" />
                                <h4>Aucun dossier trouvé</h4>
                                <p className="text-muted mb-4">Créez votre premier dossier pour commencer</p>
                                <Button variant="primary" onClick={() => navigate('/dossier/create')}>
                                    <FaPlus className="me-2" />
                                    Créer un dossier
                                </Button>
                            </>
                        ) : (
                            <>
                                <FaSearch size={60} className="text-muted mb-3" />
                                <h4>Aucun résultat</h4>
                                <p className="text-muted">Aucun dossier ne correspond à votre recherche</p>
                            </>
                        )}
                    </Card.Body>
                </Card>
            ) : (
                <Row>
                    {filteredDossiers.map((dossier) => (
                        <Col md={6} lg={4} key={dossier.id} className="mb-4">
                            <Card className="h-100 shadow-sm hover-card">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <h5 className="mb-1">
                                                <span className="me-2">{getVisaTypeIcon(dossier.visa_type)}</span>
                                                Dossier #{dossier.id}
                                            </h5>
                                            <small className="text-muted">
                                                {getVisaTypeLabel(dossier.visa_type)}
                                            </small>
                                        </div>
                                        {getStatusBadge(dossier.status)}
                                    </div>

                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between mb-2">
                                            <small className="text-muted">
                                                <FaClock className="me-1" />
                                                Créé le
                                            </small>
                                            <small className="fw-bold">
                                                {new Date(dossier.created_at).toLocaleDateString('fr-FR')}
                                            </small>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2">
                                            <small className="text-muted">Dernière mise à jour</small>
                                            <small>
                                                {new Date(dossier.updated_at).toLocaleDateString('fr-FR')}
                                            </small>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <small className="text-muted">Documents</small>
                                            <Badge bg="info" pill>
                                                {dossier.documents?.length || 0}
                                            </Badge>
                                        </div>
                                    </div>

                                    {dossier.status === 'rejected' && dossier.rejection_reason && (
                                        <Alert variant="danger" className="py-2 px-3 mb-3">
                                            <small>
                                                <strong>Rejeté :</strong> {dossier.rejection_reason}
                                            </small>
                                        </Alert>
                                    )}

                                    {dossier.admin_comment && dossier.status !== 'rejected' && (
                                        <Alert variant="info" className="py-2 px-3 mb-3">
                                            <small>
                                                <strong>Commentaire :</strong> {dossier.admin_comment}
                                            </small>
                                        </Alert>
                                    )}
                                </Card.Body>
                                <Card.Footer className="bg-white border-top">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="w-100"
                                        onClick={() => navigate(`/dossier/${dossier.id}`)}
                                    >
                                        <FaEye className="me-2" />
                                        Voir les détails
                                    </Button>
                                </Card.Footer>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
        </Container>
    );
};

export default MesDossiers;